import { createContext, useContext, useMemo, useState, useLayoutEffect } from 'react';
import { POT_POSITION } from './seatPositions';

// ---------------------------------------------------------------------------
// Live table geometry: anchor seats / bet chips / dealer button to the three
// concentric rings of the CSS-drawn table, computed from the table's MEASURED
// size so they stay glued to the felt at any window shape (full-screen,
// split-screen, any monitor) instead of drifting like the old hardcoded
// percentages did.
//
// The three rings (see .poker-table / .table-felt / .table-felt::after in
// gameroom.css) are concentric stadium/capsule shapes:
//   outer  = outer edge of .poker-table (the rail)
//   middle = the felt edge (.table-felt)
//   inner  = the thin blue bet line (.table-felt::after)
// Placement (per design):
//   player bubble -> midway between outer & middle (on the rail)
//   bet chips     -> midway between inner & middle (the felt band)
//   dealer button -> on the inner line, nudged sideways off the chip stack
// ---------------------------------------------------------------------------

// Direction of each SLOT on the felt from the table center (degrees, math convention:
// 0=right, 90=up, counter-clockwise). The four corner slots sit 60° off the
// top/bottom-center slots, pushing them out toward the table's rounded ends
// so a 6-max ring spreads across the whole rail. Tune freely.
//
// These are SLOTS, not seat numbers: which slot a given seat is drawn in is decided
// by the hero-at-the-bottom rotation below.
const SEAT_ANGLES = {
  1: 270, // bottom center
  2: 90,  // top center
  3: 150, // top left
  4: 30,  // top right
  5: 330, // bottom right
  6: 210, // bottom left
  7: 180, // left
  8: 0,   // right
  9: 250, // bottom left (9-max)
};

// ---------------------------------------------------------------------------
// Hero-at-the-bottom rotation (PURELY VISUAL)
// ---------------------------------------------------------------------------
// Every player sees their OWN seat drawn in the bottom-center slot. Nothing about the
// game changes: seat numbers, turn order, the button, the blinds and every server
// payload are untouched — only which slot on the felt a seat is PAINTED in.
//
// It must be a ROTATION of the ring, never a swap. Poker action runs around the table
// in a fixed cycle, so every seat has to keep its neighbours and its relative distance
// from every other seat; the whole table is spun until the hero lands at the bottom.
// So we walk the slots in clockwise ring order and shift EVERY seat by the same offset.
//
// SEAT_ANGLES are math angles (counter-clockwise positive), so walking them in
// DESCENDING order walks the ring CLOCKWISE — the direction action moves.
const BOTTOM_ANGLE = 270;

// Shortest angular distance between two headings, in degrees (0..180).
const angleGap = (a, b) => {
  const d = (((a - b) % 360) + 360) % 360;
  return d > 180 ? 360 - d : d;
};

// Slots 1..count in clockwise ring order, rotated to START at the slot nearest the
// bottom of the table — ring[0] is the slot the hero gets pinned to, ring[k] is k
// steps clockwise from it.
const ringOrder = (count) => {
  const slots = [];
  for (let s = 1; s <= count; s += 1) if (SEAT_ANGLES[s] != null) slots.push(s);
  if (slots.length === 0) return slots;
  slots.sort((a, b) => SEAT_ANGLES[b] - SEAT_ANGLES[a]);
  let bottom = 0;
  slots.forEach((s, i) => {
    if (angleGap(SEAT_ANGLES[s], BOTTOM_ANGLE)
        < angleGap(SEAT_ANGLES[slots[bottom]], BOTTOM_ANGLE)) bottom = i;
  });
  return slots.slice(bottom).concat(slots.slice(0, bottom));
};

// { logical seat -> display slot }, rotating `heroSeat` onto the bottom slot and carrying
// every other seat around the ring by that same offset. Empty map = identity (no rotation),
// which is what a spectator or a hero seat that isn't on this table's ring gets.
const seatToSlotMap = (count, heroSeat) => {
  const ring = ringOrder(count);
  const heroIdx = heroSeat != null ? ring.indexOf(heroSeat) : -1;
  const map = {};
  if (heroIdx < 0) return map;
  ring.forEach((seat, i) => {
    map[seat] = ring[(i - heroIdx + ring.length) % ring.length];
  });
  return map;
};

// Inner bet-line ring, mirroring `.table-felt::after { inset: 13% 7.5% }`:
// 7.5% of felt WIDTH left/right, 13% of felt HEIGHT top/bottom. Keep in sync
// with gameroom.css if that inset ever changes — the ring is drawn by a PSEUDO-element,
// which has no box in the DOM, so it can never be measured; this constant is the only way
// the JS can know where the painted line is.
const BET_LINE_INSET_X = 0.075;
const BET_LINE_INSET_Y = 0.13;

// The avatar / empty-seat circle is 4.8% of the table width (.seat / .profile-pic in
// gameroom.css — keep in sync). Its px radius drives the nameplate/bubble offsets
// below, so they scale with the responsive circle instead of assuming a fixed 60px.
const AVATAR_WIDTH_FRAC = 0.048;
// The player nameplate hangs below the avatar and OVERLAPS it: the center of its TOP edge
// sits (this fraction × the avatar radius) below the avatar's CENTER, so the plate blankets
// the circle's lower third and the overlap scales with the circle instead of being a fixed
// px tuck. Everything anchored to that top edge rides along for free — the seat's cards
// (seatAboveStyle's anchorBottom branch) and the two hole indicators (children of
// .player-info at top:50%, so they follow the plate itself).
const NAMEPLATE_DROP_FRAC = 2 / 3;
// Per-tier Power-Up pips ringing each avatar. Lives here, not in ProfilePicture.js, because
// two things need it: the component (which places each pip's CENTER at
// `avatarRadius + PIP_SIZE_PX / 4` — one avatar radius plus HALF the pip's radius, so it
// overlaps the rim by that much) and topPipCeilingPx below.
export const PIP_SIZE_PX = 32;
// Sideways slide for a table chip that has to SHARE a seat's anchor with another chip (the
// Bounty "B" chip beside the dealer "D"). Both chips RENDER at 28px — a 24px box plus a 2px
// border OUTSIDE it, since nothing sets border-box globally (same note as .pup-tier-pips in
// gameroom.css) — so 32px of center-to-center travel leaves a 4px gap. Fixed px on purpose:
// the chips are a fixed size, not a fraction of the table.
const CHIP_SIDE_OFFSET_PX = 32;
// Cards peek slightly above the avatar's center, emerging up-and-to-the-right.
const CARD_RAISE_PX = 14;
// A 2-card hand centers over the seat and tucks its bottom edge this many px BELOW the nameplate
// top, so the info bubble (higher z-index) overlaps and hides the cards' bottom edge.
const CARD_BUBBLE_OVERLAP_PX = 6;
// How far the action bubble's vertical CENTER sits above the avatar's top edge. At 0 the
// center lands right on the top rim, so the bubble's bottom half overlaps the top of the
// avatar (its higher z-index draws it over the profile pic).
const ACTION_BUBBLE_GAP_PX = 0;
// Fallback avatar radius for the pre-measurement first paint (the old fixed 60px avatar).
const FALLBACK_AVATAR_RADIUS_PX = 30;

// Static positions used for the very first paint (before the table is measured)
// and as a safety net if measurement ever fails — these are the pre-geometry
// values, so the table degrades to its old look rather than collapsing.
export const FALLBACK_SEAT_POSITIONS = {
  1: { bottom: '3%', left: '50%' }, 2: { bottom: '85%', left: '50%' },
  3: { bottom: '83%', left: '23%' }, 4: { bottom: '83%', left: '77%' },
  5: { bottom: '26%', left: '77%' }, 6: { bottom: '26%', left: '23%' },
  7: { bottom: '43%', left: '15%' }, 8: { bottom: '43%', left: '85%' },
  9: { bottom: '5%', left: '20%' },
};
export const FALLBACK_BET_POSITIONS = {
  1: { bottom: '15%', left: '50%' }, 2: { bottom: '81%', left: '50%' },
  3: { bottom: '78%', left: '33%' }, 4: { bottom: '78%', left: '67%' },
  5: { bottom: '17%', left: '67%' }, 6: { bottom: '17%', left: '33%' },
  7: { bottom: '53%', left: '19%' }, 8: { bottom: '53%', left: '81%' },
  9: { bottom: '17%', left: '27%' },
};
export const FALLBACK_DEALER_POSITIONS = {
  1: { bottom: '18%', left: '50%' }, 2: { bottom: '77%', left: '50%' },
  3: { bottom: '74%', left: '36%' }, 4: { bottom: '74%', left: '64%' },
  5: { bottom: '14%', left: '64%' }, 6: { bottom: '14%', left: '36%' },
  7: { bottom: '49%', left: '13%' }, 8: { bottom: '49%', left: '87%' },
  9: { bottom: '14%', left: '23%' },
};

// Ring-tangent per SLOT for the pre-measurement first paint (and for jsdom, where every
// getBoundingClientRect is zero): the seated player's own right, derived from the slot's
// heading on a plain circle — t = nIn rotated 90deg clockwise on screen, with the inward
// normal nIn = (-cos θ, sin θ) in screen coords. The measured capsule normal differs only on
// the oval's rounded end caps, and only for the one unmeasured frame. REQUIRED, not optional:
// without it the `view` remap below would read Object.keys(undefined) and throw.
export const FALLBACK_DEALER_TANGENTS = Object.fromEntries(
  Object.entries(SEAT_ANGLES).map(([slot, deg]) => {
    const t = (deg * Math.PI) / 180;
    return [slot, { x: -Math.sin(t), y: -Math.cos(t) }];
  })
);

const FALLBACK = {
  seatPositions: FALLBACK_SEAT_POSITIONS,
  betPositions: FALLBACK_BET_POSITIONS,
  dealerPositions: FALLBACK_DEALER_POSITIONS,
  dealerTangents: FALLBACK_DEALER_TANGENTS,
  avatarRadiusPx: FALLBACK_AVATAR_RADIUS_PX,
  // null = "not measured yet, so don't clamp anything against it". A 0 here would collapse
  // the stage/phase panel on the pre-measurement first paint (and in jsdom, where every
  // getBoundingClientRect is zero).
  topPipCeilingPx: null,
};

// Boundary point, in the direction `angleDeg` from center, of a capsule with
// half-extents A (x) and B (y) and corner radius = min(A, B). Returns an {x, y}
// offset from the capsule center in math coords (y up). Handles both a wide
// capsule (rounded left/right ends) and a tall one (rounded top/bottom).
function pointOnCapsule(A, B, angleDeg) {
  const t = (angleDeg * Math.PI) / 180;
  const ux = Math.cos(t);
  const uy = Math.sin(t);

  if (A >= B) {
    // wide capsule: flat top/bottom edges at y = ±B, end-cap circles (r=B) at (±straight, 0)
    const r = B;
    const straight = A - B;
    if (uy !== 0) {
      const k = r / Math.abs(uy);
      if (Math.abs(k * ux) <= straight) return { x: k * ux, y: k * uy };
    }
    const cx = Math.sign(ux) * straight;
    const k = ux * cx + Math.sqrt(Math.max(0, ux * ux * cx * cx - cx * cx + r * r));
    return { x: k * ux, y: k * uy };
  }
  // tall capsule: flat left/right edges at x = ±A, end-cap circles (r=A) at (0, ±straight)
  const r = A;
  const straight = B - A;
  if (ux !== 0) {
    const k = r / Math.abs(ux);
    if (Math.abs(k * uy) <= straight) return { x: k * ux, y: k * uy };
  }
  const cy = Math.sign(uy) * straight;
  const k = uy * cy + Math.sqrt(Math.max(0, uy * uy * cy * cy - cy * cy + r * r));
  return { x: k * ux, y: k * uy };
}

// Unit OUTWARD normal (perpendicular to the oval, math coords y-up) of a capsule
// with half-extents A (x), B (y) at the point `p` (relative to the capsule center).
// Flat edges → straight out (vertical for a wide capsule, horizontal for a tall one);
// rounded end-caps → radial out from that cap's circle center. Mirrors the
// flat-edge-vs-cap split in pointOnCapsule.
function capsuleNormalAt(A, B, p) {
  if (A >= B) {
    const straight = A - B;
    if (Math.abs(p.x) <= straight) return { x: 0, y: p.y >= 0 ? 1 : -1 };
    const cx = (p.x >= 0 ? 1 : -1) * straight;
    const dx = p.x - cx, dy = p.y, len = Math.hypot(dx, dy) || 1;
    return { x: dx / len, y: dy / len };
  }
  const straight = B - A;
  if (Math.abs(p.y) <= straight) return { x: p.x >= 0 ? 1 : -1, y: 0 };
  const cy = (p.y >= 0 ? 1 : -1) * straight;
  const dx = p.x, dy = p.y - cy, len = Math.hypot(dx, dy) || 1;
  return { x: dx / len, y: dy / len };
}

// Build the {seat, bet, dealer} percentage tables from the measured container,
// rail (.poker-table) and felt (.table-felt) rects. All math is in container-
// local pixels, then converted to bottom%/left% so every consumer keeps the
// same `{ bottom, left }` shape the old static tables had.
function computePositions(containerRect, railRect, feltRect) {
  const W = containerRect.width;
  const H = containerRect.height;

  // capsule {cx, cy, A, B} (center + half-extents) in container-local px
  const capsule = (rect) => ({
    cx: (rect.left + rect.right) / 2 - containerRect.left,
    cy: (rect.top + rect.bottom) / 2 - containerRect.top,
    A: rect.width / 2,
    B: rect.height / 2,
  });
  const mid = (a, b) => ({
    cx: (a.cx + b.cx) / 2, cy: (a.cy + b.cy) / 2,
    A: (a.A + b.A) / 2, B: (a.B + b.B) / 2,
  });

  const outer = capsule(railRect);
  const middle = capsule(feltRect);
  const inner = {
    cx: middle.cx,
    cy: middle.cy,
    A: (feltRect.width * (1 - 2 * BET_LINE_INSET_X)) / 2,
    B: (feltRect.height * (1 - 2 * BET_LINE_INSET_Y)) / 2,
  };

  const railRing = mid(outer, middle);   // player bubbles
  const bandRing = mid(inner, middle);   // bet chips

  // Absolute container px (y-down) for the point at `angle` on `ring`.
  const ringPointAbs = (ring, angleDeg) => {
    const p = pointOnCapsule(ring.A, ring.B, angleDeg);
    return { x: ring.cx + p.x, y: ring.cy - p.y }; // p.y is y-up; screen y is y-down
  };
  // Absolute container px -> { bottom, left } percentages of the container.
  const absToCss = (q) => ({
    left: `${((q.x / W) * 100).toFixed(3)}%`,
    bottom: `${(((H - q.y) / H) * 100).toFixed(3)}%`,
  });

  const seatPositions = {};
  const betPositions = {};
  const dealerPositions = {};
  const dealerTangents = {};
  for (const seat of Object.keys(SEAT_ANGLES)) {
    const angle = SEAT_ANGLES[seat];

    // Avatar sits on the rail ring (unchanged).
    const pRail = pointOnCapsule(railRing.A, railRing.B, angle);
    const railAbs = { x: railRing.cx + pRail.x, y: railRing.cy - pRail.y };
    seatPositions[seat] = absToCss(railAbs);

    // Inward normal (perpendicular to the oval) at the avatar, in container coords
    // (math y-up outward -> container y-down inward).
    const nOut = capsuleNormalAt(railRing.A, railRing.B, pRail);
    const nIn = { x: -nOut.x, y: nOut.y };

    // Unit vector ALONG the ring at this seat, in container px (y-down): nIn rotated 90deg
    // clockwise on screen, which lands on the SEATED PLAYER'S OWN RIGHT at every slot around
    // the table (bottom seat -> screen right, top seat -> screen left, left seat -> screen
    // down). Lets a second chip slide off the dealer anchor without changing its depth on the
    // felt — i.e. keeping its distance from the table center. See bountyStyle.
    dealerTangents[seat] = { x: -nIn.y, y: nIn.x };

    // Project the bet chip and dealer chip onto the perpendicular line through the
    // avatar: keep each one's inward depth, drop any sideways offset — so player +
    // bet chips + dealer chip end up collinear and square to the table edge.
    const projectOntoNormal = (q) => {
      const d = (q.x - railAbs.x) * nIn.x + (q.y - railAbs.y) * nIn.y;
      return { x: railAbs.x + d * nIn.x, y: railAbs.y + d * nIn.y };
    };
    betPositions[seat] = absToCss(projectOntoNormal(ringPointAbs(bandRing, angle)));
    dealerPositions[seat] = absToCss(projectOntoNormal(ringPointAbs(inner, angle)));
  }
  // Live px radius of the 4.8%-wide avatar/seat circle — drives the nameplate drop
  // and action-bubble raise so they track the responsive circle size.
  const avatarRadiusPx = (W * AVATAR_WIDTH_FRAC) / 2;

  // Y (from the container's TOP edge) of the top edge of the highest tier pip drawn at the
  // TOP-CENTER slot — i.e. the ceiling anything docked to the top of the table must fit
  // under. That slot is always angle 90 (see SEAT_ANGLES); the hero-at-the-bottom rotation
  // changes only WHICH SEAT is painted there, never where the slot is, so one number covers
  // every rotation and it can be read straight off railRing's top.
  const topAvatarCy = railRing.cy - railRing.B;
  const topPipCeilingPx = Math.max(0, topAvatarCy
    - (avatarRadiusPx + PIP_SIZE_PX / 4)   // pip CENTER offset (see ProfilePicture)
    - PIP_SIZE_PX / 2);                    // + the pip's own radius
  return { seatPositions, betPositions, dealerPositions, dealerTangents,
           avatarRadiusPx, topPipCeilingPx };
}

// Hook: measures the table and returns the computed position tables plus inline
// style helpers (same return shapes the old seatPositions.js helpers had).
//
// `heroSeat` (the viewer's own seat, or null when spectating) and `seatCount` drive the
// hero-at-the-bottom rotation above. Everything this hook returns is keyed by LOGICAL
// seat number, so every consumer keeps passing real seat numbers and simply lands in the
// rotated slot — the rotation exists only inside here.
export const useTableGeometry = ({ containerRef, railRef, feltRef, heroSeat = null, seatCount = 6 }) => {
  const [positions, setPositions] = useState(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const rail = railRef.current;
    const felt = feltRef.current;
    if (!container || !rail || !felt) return undefined;

    const measure = () => {
      const c = container.getBoundingClientRect();
      const r = rail.getBoundingClientRect();
      const f = felt.getBoundingClientRect();
      if (!c.width || !c.height || !f.width || !f.height) return;
      setPositions(computePositions(c, r, f));
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
    // refs are stable; measure reads them live on every resize.
  }, [containerRef, railRef, feltRef]);

  const pos = positions || FALLBACK;

  // computePositions (and the FALLBACK_* tables) are keyed by display SLOT. Re-key them by
  // LOGICAL seat here, once, so every helper and every raw table below is already rotated
  // and no consumer has to know the rotation exists. Memoized so `seatPositions` keeps a
  // stable identity for the components that take it as a prop.
  const view = useMemo(() => {
    const map = seatToSlotMap(seatCount, heroSeat);
    const remap = (table) => {
      const out = {};
      Object.keys(table).forEach((key) => {
        const seat = Number(key);
        const slot = map[seat] != null ? map[seat] : seat;
        if (table[slot]) out[seat] = table[slot];
      });
      return out;
    };
    return {
      seatPositions: remap(pos.seatPositions),
      betPositions: remap(pos.betPositions),
      dealerPositions: remap(pos.dealerPositions),
      // Slot-keyed exactly like dealerPositions, so it MUST go through the identical remap:
      // a rotated seat that got one slot's anchor with another slot's tangent would fling
      // the bounty chip off in the wrong direction.
      dealerTangents: remap(pos.dealerTangents),
    };
  }, [pos, seatCount, heroSeat]);

  // Offsets tied to the avatar circle's live size (see AVATAR_WIDTH_FRAC).
  const avatarRadiusPx = pos.avatarRadiusPx;
  const nameplateDropPx = avatarRadiusPx * NAMEPLATE_DROP_FRAC;
  const actionBubbleRaisePx = avatarRadiusPx + ACTION_BUBBLE_GAP_PX;

  // Centers an element on its (left, bottom) anchor point.
  const centered = 'translateX(-50%) translateY(50%)';

  return {
    // Rotated and keyed by LOGICAL seat (see `view` above) — consumers that index these
    // directly (chip flights, the Power-Up target lines, the "+N" win floats) pass real
    // seat numbers and get the slot that seat is drawn in.
    seatPositions: view.seatPositions,
    betPositions: view.betPositions,
    dealerPositions: view.dealerPositions,
    // Ring tangent per seat (the seated player's right), paired with dealerPositions above.
    // Exposed for tests and for anything that needs to fan a chip sideways — see bountyStyle.
    dealerTangents: view.dealerTangents,
    potPosition: POT_POSITION,
    avatarRadiusPx,
    // Slot-space and rotation-invariant, so it deliberately does NOT go through the `view`
    // remap above. Consumed by StagePhaseIndicator (via GameTable) to clamp the top-docked
    // stage panel so it never covers the top seat's Power-Up counts.
    topPipCeilingPx: pos.topPipCeilingPx,

    // The player bubble / empty-seat button, centered on the rail ring.
    seatStyle: (seat) => {
      const p = view.seatPositions[seat];
      return p ? { ...p, transform: centered } : undefined;
    },
    // Bet-chip stack, centered in the felt band between the bet line and felt edge.
    betStyle: (seat) => {
      const p = view.betPositions[seat];
      return p ? { ...p, transform: centered } : undefined;
    },
    // Dealer button, centered on the inner bet line.
    dealerStyle: (seat) => {
      const p = view.dealerPositions[seat];
      return p ? { ...p, transform: centered } : undefined;
    },
    // Bounty button token — the SAME anchor as the dealer button, so a seat holding only the
    // bounty wears it exactly where a D chip would sit. `besideDealer` (that seat holds BOTH)
    // slides it one chip-width ALONG the ring — perpendicular to the inward normal, so its
    // depth on the felt, and therefore its distance from the table center, is unchanged — to
    // the seated player's own RIGHT. The D chip never moves; only the B yields.
    bountyStyle: (seat, besideDealer = false) => {
      const p = view.dealerPositions[seat];
      if (!p) return undefined;
      if (!besideDealer) return { ...p, transform: centered };
      const t = view.dealerTangents[seat] || { x: 1, y: 0 };
      // Snap float noise to zero: an axis-aligned tangent lands on ~1e-16 rather than a clean
      // 0, and the raw toFixed of a tiny NEGATIVE would emit "- -0.00px".
      const px = (v) => `${(Math.abs(v) < 0.005 ? 0 : v).toFixed(2)}px`;
      return {
        left: `calc(${p.left} + ${px(t.x * CHIP_SIDE_OFFSET_PX)})`,
        // `bottom` grows UP the screen while the tangent's y grows DOWN — hence the subtract.
        bottom: `calc(${p.bottom} - ${px(t.y * CHIP_SIDE_OFFSET_PX)})`,
        transform: centered,
      };
    },
    // Circular profile picture: centered on the rail ring, exactly where the
    // empty-seat circle sits, so an occupied seat lands in the same spot.
    seatProfileStyle: (seat) => {
      const p = view.seatPositions[seat];
      return p ? { ...p, transform: centered } : undefined;
    },
    // Player nameplate: overlaps the lower part of the avatar for every seat and grows
    // downward — translateY(100%) pins its TOP edge exactly nameplateDropPx (= 2/3 of the
    // avatar radius) below the avatar's CENTER.
    seatNameplateStyle: (seat) => {
      const p = view.seatPositions[seat];
      if (!p) return undefined;
      return {
        bottom: `calc(${p.bottom} - ${nameplateDropPx}px)`,
        left: p.left,
        transform: 'translateX(-50%) translateY(100%)',
      };
    },
    // A row of cards above the avatar.
    //   { center: true }        — centers the row directly over the avatar/nameplate (so a 2-card
    //                             hand or a wide PLO fan blankets the profile pic) instead of the
    //                             legacy up-and-to-the-right peek.
    //   { anchorBottom: true }  — pins the BOTTOM edge of the card row to the TOP of the nameplate
    //                             (the player info bubble), growing upward, so the row's bottom
    //                             lands on the bubble regardless of card height (used by the small
    //                             opponent "mini" cards and by every 2-card hand).
    //   { hideBottomEdge: true }— tucks that bottom edge a few px BELOW the nameplate top, so the
    //                             bubble (higher z-index) overlaps and hides the cards' bottom edge.
    seatAboveStyle: (seat, { center = false, anchorBottom = false, hideBottomEdge = false } = {}) => {
      const p = view.seatPositions[seat];
      if (!p) return undefined;
      if (anchorBottom) {
        // Card row's bottom edge sits on the nameplate top (p.bottom - nameplateDropPx), growing
        // upward — no translateY, so the anchor IS the bottom edge for any card height. hideBottomEdge
        // drops it a few px further so the nameplate overlaps and hides that bottom edge.
        const drop = nameplateDropPx + (hideBottomEdge ? CARD_BUBBLE_OVERLAP_PX : 0);
        return {
          bottom: `calc(${p.bottom} - ${drop}px)`,
          left: p.left,
          transform: center ? 'translateX(-50%)' : 'translateX(22px)',
        };
      }
      return {
        bottom: `calc(${p.bottom} + ${CARD_RAISE_PX}px)`,
        left: p.left,
        transform: center ? 'translateX(-50%) translateY(50%)' : 'translateX(22px) translateY(50%)',
      };
    },
    // Action bubble ("Call"/"Bet"/"Raise"/etc.), horizontally centered, sitting with its bottom
    // half overlapping the top of the avatar (center on the avatar's top rim via actionBubbleRaisePx).
    seatActionBubbleStyle: (seat) => {
      const p = view.seatPositions[seat];
      if (!p) return undefined;
      return {
        bottom: `calc(${p.bottom} + ${actionBubbleRaisePx}px)`,
        left: p.left,
        transform: 'translateX(-50%) translateY(50%)',
      };
    },
    // A row of cards just to the right of the seat, vertically centered on it.
    seatRightStyle: (seat) => {
      const p = view.seatPositions[seat];
      if (!p) return undefined;
      return { bottom: p.bottom, left: `calc(${p.left} + 4%)`, transform: 'translateY(50%)' };
    },
    // A PERMANENT Power-Up card parked to the LEFT of the seat, in the bet band (the annulus
    // between the inner bet line and the felt edge — the same ring the bet chips use), offset
    // sideways so it doesn't collide with the chips. `index` fans multiple cards further left.
    permanentStyle: (seat, index = 0) => {
      const p = view.betPositions[seat];
      if (!p) return undefined;
      const shiftPct = 6 + index * 3.4; // % of container width, one card-width further left each
      return { bottom: p.bottom, left: `calc(${p.left} - ${shiftPct}%)`, transform: centered };
    },
  };
};

// Context so deep children (Player, ProfilePicture, SeatCards, HoleCards,
// DealerDraw) read the live geometry without prop-drilling. GameTable provides
// the value returned by useTableGeometry.
export const TableGeometryContext = createContext(null);

export const useTableGeometryCtx = () => {
  const ctx = useContext(TableGeometryContext);
  if (!ctx) throw new Error('useTableGeometryCtx must be used within a TableGeometryContext provider');
  return ctx;
};
